/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';

import {
  CHAMPS_INDIVIDU_FICHE,
  SEXE_CODE_BY_SEXE,
  SEXE_LIBELLE_BY_SEXE,
  formatDateNaissance,
  formatLieuIndividuFiche,
  formatAdressePrefixee,
  formatLieuIndividuPrefixe,
  formatSexe,
} from './formatters';
import { parseSexe } from './parsers';

/**
 * Sous-objet individu des payloads de mise à jour de la fiche acte.
 *
 * Le PUT **remplace** le sous-objet : on repart de l'individu tel qu'il a été lu
 * et on écrase avec les champs édités. Corollaire utile — un champ hors du
 * périmètre d'une modale est `undefined`, donc retiré par le `_.omitBy`, et
 * conserve mécaniquement sa valeur lue.
 *
 * @param individu Individu tel que reçu en lecture dans la fiche (à plat).
 * @param data État du formulaire de la modal (shape des composants de la lib :
 * `jour/mois/anneeNaissance`, `sexe`, `adresseNaissance` imbriquée).
 */
/**
 * Champs de civilité, retirés de la reprise telle quelle et toujours recalculés.
 *
 * ⚠️ Les valeurs LUES ne sont pas celles qu'on écrit : le modèle rend
 * `sexe = 'M.' | 'Mme'` et `sexeCode = 'Monsieur' | 'Madame'`, là où les
 * colonnes attendent un entier (1 / 2) et une lettre (`M` / `F` / `I`). Les
 * réémettre donne un HTTP 500 sur `ai_sexe` (`SQLSTATE[22P02]`) et, plus
 * sournois, un `ai_sexec` à « Monsieur » qu'aucune erreur ne signale.
 *
 * Le piège ne se voit qu'avec une modale qui n'édite PAS la civilité : sinon
 * `data.sexe` écrase toujours la valeur lue.
 */
const CHAMPS_CIVILITE = ['sexe', 'sexeCode', 'sexeLibelle'];

const buildIndividuUpdatePayload = (
  individu: Record<string, any> | undefined,
  data: Record<string, any>,
) => {
  /*
   * Civilité éditée si la modale la propose, sinon RELUE et réencodée depuis
   * l'individu : dans les deux cas elle repart au format des colonnes.
   */
  const sexe = data?.sexe ?? parseSexe(individu);

  return {
    ..._.omit(_.pick(individu, CHAMPS_INDIVIDU_FICHE), CHAMPS_CIVILITE),
    ..._.omitBy(
      {
        nomFamille: data?.nomFamille,
        nomUsage: data?.nomUsage,
        prenom: data?.prenom,
        prenomsComplementaires: data?.prenomsComplementaires,
        sexe: formatSexe(sexe),
        sexeCode: sexe ? SEXE_CODE_BY_SEXE[sexe] : undefined,
        // `ai_sexel` : recalculé par le PUT du dossier (naissance), mais c'est ce
        // payload qui le porte lorsqu'on passe par `PUT /acte-individus/{id}`.
        sexeLibelle: sexe ? SEXE_LIBELLE_BY_SEXE[sexe] : undefined,
        naissanceDate: formatDateNaissance(data),
        ...(data?.adresseNaissance &&
          formatLieuIndividuFiche(data.adresseNaissance)),
        profession: data?.profession,
        /*
         * Champs propres au décès. La garde `data?.adresseX &&` compte : sans
         * elle, une modale qui n'édite pas ce lieu enverrait cinq `undefined`,
         * que le `_.omitBy` retirerait — mais l'objet vide, lui, écraserait le
         * lieu lu. C'est la même précaution que pour `adresseNaissance`.
         */
        age: data?.age,
        ...(data?.adresseDeces &&
          formatLieuIndividuPrefixe(data.adresseDeces, 'deces')),
        // Le domicile est une adresse complète (voie incluse), le lieu de décès
        // un simple lieu : deux formateurs distincts, volontairement.
        ...(data?.adresseDomicile &&
          formatAdressePrefixee(data.adresseDomicile, 'domicile')),
      },
      _.isUndefined,
    ),
    identifiant: individu?.identifiant,
  };
};

export default buildIndividuUpdatePayload;
